import type {
  NewSealedProduct,
  SealedProduct,
  SealedProductListFilter,
  SealedStockUpdate,
} from "@hobbyshopify/backend-client";
import type { Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  archiveSealedProduct,
  createSealedProduct,
  loadSealedProducts,
  setSealedStock,
} from "../../core/ops/sealed-ops";
import { errorText } from "../../core/primitives/errors";
import { Loader } from "../../core/primitives/loader";
import { SealedActions, type SealedDialog, SealedState } from "./sealed-model";

export type SealedDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

interface SealedQuery {
  readonly shopId: string;
  readonly filter: SealedProductListFilter;
  readonly search: string;
}

/** The shop's sealed product: what it breaks, counted the same way its cards are. */
export class SealedController extends SubtreeController implements SealedActions {
  readonly state = new SealedState();
  readonly #list: Loader<SealedQuery, SealedProduct[]>;
  #shopId: string | null = null;

  constructor(readonly deps: SealedDeps) {
    super();
    this.subtree.put(SealedState, this.state);
    this.subtree.put(SealedActions, this);
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        fetch: (query: SealedQuery) => loadSealedProducts({ ...deps, ...query }),
        loaded: (items) => {
          this.state.items.value = items;
        },
      }),
    );
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      this.state.items.value = [];
      void this.#reload();
    }, [deps.storeRepository]);
  }

  setFilter = (filter: SealedProductListFilter) => {
    this.state.filter.value = filter;
    void this.#reload();
  };

  setSearch = (search: string) => {
    this.state.search.value = search;
    const query = this.#query();
    if (!query) return;
    if (search) this.#list.loadDebounced(query);
    else void this.#list.load(query);
  };

  openAdd = () => this.#open({ kind: "add" });
  openStock = (product: SealedProduct) => this.#open({ kind: "stock", product });
  openArchive = (product: SealedProduct) => this.#open({ kind: "archive", product });

  closeDialog = () => {
    this.state.dialog.value = null;
  };

  saveNew = async (product: NewSealedProduct) => {
    await this.#runDialog("add", (shopId) => createSealedProduct({ ...this.deps, shopId, product }));
  };

  saveStock = async (change: SealedStockUpdate) => {
    await this.#runDialog("stock", (shopId, product) =>
      setSealedStock({ ...this.deps, shopId, sealedProductId: (product as SealedProduct).id, change }),
    );
  };

  confirmArchive = async () => {
    await this.#runDialog("archive", (shopId, product) =>
      archiveSealedProduct({ ...this.deps, shopId, sealedProductId: (product as SealedProduct).id }),
    );
  };

  #open(dialog: SealedDialog): void {
    this.state.dialogError.value = null;
    this.state.dialog.value = dialog;
  }

  async #runDialog(
    kind: SealedDialog["kind"],
    action: (shopId: string, product: SealedProduct | null) => Promise<Result<unknown, Error>>,
  ): Promise<void> {
    const dialog = this.state.dialog.value;
    const shopId = this.#shopId;
    if (!dialog || dialog.kind !== kind || shopId === null || this.state.dialogBusy.value) return;
    const product = dialog.kind === "add" ? null : dialog.product;
    this.state.busyId.value = product?.id ?? null;
    this.state.dialogBusy.value = true;
    this.state.dialogError.value = null;
    try {
      const r = await action(shopId, product);
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.dialogError.value = errorText(r.errors);
        return;
      }
      this.state.dialog.value = null;
      await this.#reload();
    } finally {
      this.state.busyId.value = null;
      this.state.dialogBusy.value = false;
    }
  }

  retry = () => {
    void this.#reload();
  };

  async #reload(): Promise<void> {
    const query = this.#query();
    if (query) await this.#list.load(query);
  }

  #query(): SealedQuery | null {
    if (this.#shopId === null) return null;
    return { shopId: this.#shopId, filter: this.state.filter.value, search: this.state.search.value };
  }
}
